import React, { useEffect, useState } from 'react';

/**
 * § 396 — a live region must exist before its words do.
 *
 * A screen reader speaks a live region when its content CHANGES. A region that arrives in the
 * page already holding its words has not changed, and many screen reader / browser pairs say
 * nothing: NVDA typically does not announce a `role="status"` inserted with its text, and JAWS
 * and VoiceOver do so inconsistently. That was every wait in this design system — a Spinner
 * mounts with "Loading" inside its own status (and so do Button, IconButton, Search,
 * SidePanel, TreeView and Table's rows and RefreshOverlay, which show one); Table's
 * first-load status, PageSkeleton's and SelectionBar's first count mount the same way.
 *
 * **Why not one shared announcer.** The textbook fix is a single region, mounted once, that
 * components post messages to. Here it would mean taking `role="status"` off those
 * components, and 41 queries in 25 test files (20 of them app screens') — and every spec
 * written from `okf/design-system/e2e.md` — find the waits by that role. So the region stays where it is and
 * the words wait instead: they are in the DOM from the first render (a test, a spec and the eye
 * all see them at once) but hidden from the accessibility tree for the region's first
 * ANNOUNCE_DELAY_MS. A screen reader meets the region empty, then hears the words arrive — a
 * change, which every pairing announces.
 *
 * 100ms, not one frame: a region must be registered by the screen reader, not merely laid out
 * by the browser, before its change can be heard — React Aria's announcer and Downshift's
 * status make the same wait for the same reason. It is below what anyone perceives as a delay,
 * and a wait that ends sooner than that had nothing worth announcing.
 *
 * A region that is ALWAYS mounted needs none of this — TextField's copy status (§ 303) and
 * Combobox's result count are, and their words simply change.
 */
export const ANNOUNCE_DELAY_MS = 100;

/**
 * False on mount, true ANNOUNCE_DELAY_MS later: whether a live region that mounted with its
 * words may expose them yet. Call it in the component that mounts WITH the region, so the
 * delay starts when the region does.
 */
export const useLiveRegionReady = (): boolean => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), ANNOUNCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  return ready;
};

/**
 * The words of a live region that mounts with them, for a region that lives inside a
 * longer-lived component (Table's first-load status): its own mount starts the delay, so the
 * wait is timed from the region, not from the table.
 */
export const LiveRegionWords = ({ children }: { children: React.ReactNode }) => {
  const ready = useLiveRegionReady();
  return <span aria-hidden={ready ? undefined : true}>{children}</span>;
};

/**
 * § 396 — how long a typed query must be still before its result count is spoken. The screen
 * reader is echoing every character typed; a count on each keystroke would talk over that echo
 * and be cut off by the next one. Half a second is the pause between words — between
 * GOV.UK's accessible autocomplete (1.4s) and Downshift's status message (200ms).
 *
 * § 406 — moved here from Combobox, which still re-exports it, so a searchable Dropdown speaks
 * its count on the same clock.
 */
export const RESULTS_ANNOUNCE_MS = 500;

/** The words for how many options a query left. Combobox's `resultsAnnouncement` default. */
export const announceResultCount = (count: number): string =>
  (count === 0 ? 'No options available' : `${count} ${count === 1 ? 'option' : 'options'} available`);

/**
 * `words`, once they have been unchanged for RESULTS_ANNOUNCE_MS — the text for a status region
 * that is ALREADY mounted. Empty words empty it at once: a query that was cleared has nothing
 * left to report, and a stale count must not be spoken after it.
 */
export const useSettledWords = (words: string): string => {
  const [spoken, setSpoken] = useState('');
  useEffect(() => {
    if (!words) {
      setSpoken('');
      return undefined;
    }
    const timer = setTimeout(() => setSpoken(words), RESULTS_ANNOUNCE_MS);
    return () => clearTimeout(timer);
  }, [words]);
  return spoken;
};
